import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { sa } from '../../superAdminUi';
import { badgeSx, hintSx, noteSx, tone } from './panelStyles';

const rowLabelSx = { fontFamily: sa.font.family, fontSize: 12.5, color: sa.color.textGray, mb: 0.5 };
const valueSx = { fontFamily: sa.font.family, fontSize: 14, color: sa.color.text };

// The before → after summary shown before anything is written. Access changes take effect
// straight away, so the operator sees exactly what will change — and what won't.
function ReviewChanges({ name, roles, schools, keepsRestrictions, syncsDashboards }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography sx={hintSx}>Check the changes below. They take effect as soon as you save.</Typography>

      {name && (
        <Box>
          <Typography sx={rowLabelSx}>Name</Typography>
          <Box sx={{ ...valueSx, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <span style={{ color: sa.color.textGray }}>{name.before}</span>
            <ArrowForwardIcon sx={{ fontSize: 16, color: sa.color.textGray }} />
            <span>{name.after}</span>
          </Box>
        </Box>
      )}

      {schools && (
        <Box>
          <Typography sx={rowLabelSx}>Schools</Typography>
          <Box sx={{ ...valueSx, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <span style={{ color: sa.color.textGray }}>{schools.before}</span>
            <ArrowForwardIcon sx={{ fontSize: 16, color: sa.color.textGray }} />
            <span>{schools.after}</span>
          </Box>
        </Box>
      )}

      {roles && (
        <Box>
          <Typography sx={rowLabelSx}>Roles</Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {roles.added.map((roleName) => (
              <Box key={`+${roleName}`} component='span' sx={badgeSx(tone.added)}>+ {roleName}</Box>
            ))}
            {roles.removed.map((roleName) => (
              <Box key={`-${roleName}`} component='span' sx={badgeSx(tone.removed)}>− {roleName}</Box>
            ))}
          </Box>
        </Box>
      )}

      {(keepsRestrictions || syncsDashboards) && (
        <Box sx={noteSx()}>
          {keepsRestrictions && <div>Data restrictions on this user’s access stay as they are.</div>}
          {syncsDashboards && <div>Their dashboards will be updated to match.</div>}
        </Box>
      )}
    </Box>
  );
}

const changePair = PropTypes.shape({ before: PropTypes.string, after: PropTypes.string });

ReviewChanges.propTypes = {
  name: changePair,
  schools: changePair,
  roles: PropTypes.shape({
    added: PropTypes.arrayOf(PropTypes.string).isRequired,
    removed: PropTypes.arrayOf(PropTypes.string).isRequired,
  }),
  keepsRestrictions: PropTypes.bool,
  syncsDashboards: PropTypes.bool,
};

export default ReviewChanges;
